import { useCallback, useEffect, useMemo, useState } from "react";

import type { ChatMessage, CrmChatRecordContext } from "../types/chat.types";

const STORAGE_VERSION = "v1";
const STORAGE_PREFIX = "crm-chat-session";

interface StoredSession {
  version: string;
  workspaceId: string;
  sessionId: string;
  recordId?: string;
  messages: ChatMessage[];
  promptType?: string | null;
  promptName?: string | null;
  savedAt: number;
}

function newSessionId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function makeKey(workspaceId: string, record?: CrmChatRecordContext | null): string {
  const recordSlug = record ? `${record.recordType}:${record.recordId}` : "workspace";
  return `${STORAGE_PREFIX}:${STORAGE_VERSION}:${workspaceId}:${recordSlug}`;
}

interface UseCrmChatSessionArgs {
  workspaceId: string;
  record?: CrmChatRecordContext | null;
}

interface UseCrmChatSessionResult {
  sessionId: string;
  initialMessages: ChatMessage[];
  initialPromptType: string | null;
  initialPromptName: string | null;
  persist: (snapshot: {
    messages: ChatMessage[];
    promptType?: string | null;
    promptName?: string | null;
  }) => void;
  clear: () => void;
  resetSessionId: () => string;
}

/**
 * Lightweight session persistence keyed on workspace + record. Phase-1
 * answers the spec's open question (§15.2): yes, we persist locally so the
 * user can re-open the drawer and continue where they left off until the
 * backend chat session API lands.
 */
export function useCrmChatSession({
  workspaceId,
  record,
}: UseCrmChatSessionArgs): UseCrmChatSessionResult {
  const key = useMemo(() => makeKey(workspaceId, record), [workspaceId, record]);
  const [snapshot, setSnapshot] = useState<StoredSession | null>(() => readSnapshot(key));
  const [sessionId, setSessionId] = useState<string>(
    () => readSnapshot(key)?.sessionId ?? newSessionId()
  );

  useEffect(() => {
    const loaded = readSnapshot(key);
    setSnapshot(loaded);
    setSessionId(loaded?.sessionId ?? newSessionId());
  }, [key]);

  const persist = useCallback<UseCrmChatSessionResult["persist"]>(
    ({ messages, promptType, promptName }) => {
      try {
        const payload: StoredSession = {
          version: STORAGE_VERSION,
          workspaceId,
          sessionId,
          recordId: record?.recordId,
          messages,
          promptType: promptType ?? null,
          promptName: promptName ?? null,
          savedAt: Date.now(),
        };
        window.sessionStorage.setItem(key, JSON.stringify(payload));
        setSnapshot(payload);
      } catch {
        // Storage may be unavailable (private mode, quota); fail silently.
      }
    },
    [key, record?.recordId, sessionId, workspaceId]
  );

  const clear = useCallback(() => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
    setSnapshot(null);
    setSessionId(newSessionId());
  }, [key]);

  const resetSessionId = useCallback(() => {
    const id = newSessionId();
    setSessionId(id);
    return id;
  }, []);

  return useMemo<UseCrmChatSessionResult>(
    () => ({
      sessionId,
      initialMessages: snapshot?.messages ?? [],
      initialPromptType: snapshot?.promptType ?? null,
      initialPromptName: snapshot?.promptName ?? null,
      persist,
      clear,
      resetSessionId,
    }),
    [sessionId, snapshot, persist, clear, resetSessionId]
  );
}

function readSnapshot(key: string): StoredSession | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (parsed.version !== STORAGE_VERSION) return null;
    if (!parsed.sessionId) {
      parsed.sessionId = newSessionId();
    }
    return parsed;
  } catch {
    return null;
  }
}
